import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { PlayArrow as PlayArrowIcon } from '@mui/icons-material';
import {
  getCompanyContentDetail,
} from '../../api/contentCompletion';
import {
  CompanyContentDetail,
  CompanyContentHealth,
} from '../../types/contentCompletion';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage/ErrorMessage';
import BackfillPreviewDialog from './BackfillPreviewDialog';
import EditableFieldCell from './EditableFieldCell';
import EditableSelectCell from './EditableSelectCell';
import EditableFormerNamesCell from './EditableFormerNamesCell';
import AcquiredByCompanyCell from './AcquiredByCompanyCell';
import { resolveDefaultSourceUrl } from './backfillUtils';
import { CompanyUpdate } from '../../types/company';
import {
  CORPORATE_STATUS_OPTIONS,
  formatCorporateStatus,
  isInactiveCorporateStatus,
} from '../../constants/corporateStatus';
import {
  COVERAGE_PRIORITY_OPTIONS,
  formatCoveragePriority,
} from '../../constants/coveragePriority';
import {
  PR_STATUS_LABELS,
  QUARTR_STATUS_LABELS,
  formatLastContentDate,
  prStatusTooltip,
  quartrStatusTooltip,
} from './contentHealthLabels';

type EditableCompanyField = keyof Pick<
  CompanyUpdate,
  | 'corporate_status'
  | 'coverage_priority'
  | 'former_names'
  | 'acquired_by_company_id'
  | 'feed_news_rss'
  | 'press_release_url'
  | 'feed_press_release'
  | 'quartr_company_id'
>;

interface CompanyDetailDrawerProps {
  company: CompanyContentHealth | null;
  staleDays: number;
  canEdit: boolean;
  onClose: () => void;
  onRefreshOverview: () => void;
  onIssueReported?: () => void;
  onBackfillComplete?: () => void;
  onFieldSave: (
    companyId: string,
    field: EditableCompanyField,
    value: string | string[],
    options?: { acquiredByCompanyName?: string }
  ) => Promise<void>;
}

const formatDateTime = (value?: string) => {
  if (!value) return '—';
  // Calendar published dates: show the UTC date so midnight UTC does not
  // render as the previous day in US local timezones.
  const dateOnly = value.match(/^(\d{4}-\d{2}-\d{2})/);
  if (dateOnly) {
    const [y, m, d] = dateOnly[1].split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
      timeZone: 'UTC',
    });
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString();
};

const drawerPrLabel = (status: keyof typeof PR_STATUS_LABELS) =>
  `PR ${PR_STATUS_LABELS[status]}`;

const drawerQuartrLabel = (status: keyof typeof QUARTR_STATUS_LABELS) =>
  `Quartr ${QUARTR_STATUS_LABELS[status]}`;

type FeedUrlField = keyof Pick<
  CompanyUpdate,
  'feed_news_rss' | 'press_release_url' | 'feed_press_release' | 'quartr_company_id'
>;

const FEED_FIELDS: Array<{
  label: string;
  field: FeedUrlField;
  isUrl: boolean;
  allowAdd?: boolean;
}> = [
  { label: 'News RSS', field: 'feed_news_rss', isUrl: true },
  { label: 'Press release page', field: 'press_release_url', isUrl: true },
  { label: 'Press release feed', field: 'feed_press_release', isUrl: true, allowAdd: false },
  { label: 'Quartr ID', field: 'quartr_company_id', isUrl: false },
];

const CompanyDetailDrawer: React.FC<CompanyDetailDrawerProps> = ({
  company,
  staleDays,
  canEdit,
  onClose,
  onRefreshOverview,
  onIssueReported,
  onBackfillComplete,
  onFieldSave,
}) => {
  const [detail, setDetail] = useState<CompanyContentDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [backfillOpen, setBackfillOpen] = useState(false);

  const companyId = company?.company_id;

  const loadDetail = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getCompanyContentDetail(companyId, staleDays);
      setDetail(data);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err as Error)?.message ||
        'Failed to load company detail';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [companyId, staleDays]);

  useEffect(() => {
    if (companyId) {
      loadDetail();
    } else {
      setDetail(null);
      setError(null);
    }
  }, [companyId, loadDetail]);

  const defaultSourceUrl = company ? resolveDefaultSourceUrl(company) : '';

  if (!company) {
    return null;
  }

  const health = detail?.health ?? company;

  const handleFieldSave = async (
    companyId: string,
    value: string | string[],
    field: EditableCompanyField,
    options?: { acquiredByCompanyName?: string }
  ) => {
    await onFieldSave(companyId, field, value, options);
  };

  const inactiveCompany = isInactiveCorporateStatus(company.corporate_status);

  return (
    <>
      <Drawer anchor="right" open={Boolean(company)} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}>
        {company && (
          <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              {company.company_name}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {company.company_id}
              {company.ticker ? ` · ${company.ticker}` : ''}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', my: 2 }}>
              <Tooltip title={prStatusTooltip(health.status, staleDays, health.last_pr_date)}>
                <Chip
                  label={drawerPrLabel(health.status)}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Tooltip>
              <Tooltip
                title={quartrStatusTooltip(
                  health.quartr_status,
                  staleDays,
                  health.last_quartr_date
                )}
              >
                <Chip
                  label={drawerQuartrLabel(health.quartr_status)}
                  size="small"
                  color={health.quartr_status === 'missing_id' ? 'info' : 'secondary'}
                  variant="outlined"
                />
              </Tooltip>
              <Chip label={`PR total: ${health.pr_total_count}`} size="small" />
              <Chip label={`Quartr: ${health.quartr_total_count}`} size="small" />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Last PR: {formatLastContentDate(health.last_pr_date)}
              {' · '}
              Last Quartr: {formatLastContentDate(health.last_quartr_date)}
            </Typography>

            {health.quartr_status === 'missing_id' && (
              <Typography variant="caption" color="info.main" sx={{ display: 'block', mb: 2 }}>
                Public company with a ticker but no Quartr company ID — select the company and run
                Quartr check on the Content Completion page.
              </Typography>
            )}

            <Typography variant="subtitle2" gutterBottom>
              Company details
            </Typography>
            <Box sx={{ mb: 2 }}>
              <EditableSelectCell
                companyId={company.company_id}
                value={company.corporate_status}
                label="Corporate status"
                options={[...CORPORATE_STATUS_OPTIONS]}
                canEdit={canEdit}
                formatValue={formatCorporateStatus}
                onSave={(companyId, value) => handleFieldSave(companyId, value, 'corporate_status')}
              />
              <EditableSelectCell
                companyId={company.company_id}
                value={company.coverage_priority}
                label="Coverage priority"
                options={[...COVERAGE_PRIORITY_OPTIONS]}
                canEdit={canEdit}
                formatValue={formatCoveragePriority}
                onSave={(companyId, value) => handleFieldSave(companyId, value, 'coverage_priority')}
              />
              <EditableFormerNamesCell
                companyId={company.company_id}
                value={company.former_names}
                canEdit={canEdit}
                onSave={(companyId, value) => handleFieldSave(companyId, value, 'former_names')}
              />
              <AcquiredByCompanyCell
                companyId={company.company_id}
                acquiredByCompanyId={company.acquired_by_company_id}
                acquiredByCompanyName={company.acquired_by_company_name}
                canEdit={canEdit}
                onSave={(companyId, acquiredByCompanyId, acquiredByCompanyName) =>
                  handleFieldSave(companyId, acquiredByCompanyId, 'acquired_by_company_id', {
                    acquiredByCompanyName,
                  })
                }
              />
            </Box>

            {inactiveCompany && (
              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 2 }}>
                This company is marked inactive — automatic feed repair and backfill are disabled.
              </Typography>
            )}

            <Typography variant="subtitle2" gutterBottom>
              Feed URLs
            </Typography>
            <Box sx={{ mb: 2 }}>
              {FEED_FIELDS.map(({ label, field, isUrl, allowAdd }) => (
                <EditableFieldCell
                  key={field}
                  companyId={company.company_id}
                  value={company[field]}
                  label={label}
                  canEdit={canEdit}
                  isUrl={isUrl}
                  allowAdd={allowAdd}
                  allowClear
                  placeholder={isUrl ? 'Paste URL…' : 'e.g. 6220'}
                  onSave={(companyId, value) => handleFieldSave(companyId, value, field)}
                />
              ))}
            </Box>

            <Divider sx={{ my: 1 }} />

            {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}

            {loading ? (
              <LoadingSpinner message="Loading recent content..." />
            ) : (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  Recent content in Neo4j
                </Typography>
                <List dense sx={{ flex: 1, overflow: 'auto', mb: 2 }}>
                  {(detail?.recent_content || []).length === 0 ? (
                    <ListItem>
                      <ListItemText primary="No content found for this company." />
                    </ListItem>
                  ) : (
                    detail?.recent_content.map((item) => (
                      <ListItem key={item.id} alignItems="flex-start" sx={{ px: 0 }}>
                        <ListItemText
                          primary={item.title || item.url || item.id}
                          secondary={
                            <>
                              {formatDateTime(item.published_date)}
                              {item.data_source_type ? ` · ${item.data_source_type}` : ''}
                            </>
                          }
                        />
                      </ListItem>
                    ))
                  )}
                </List>
              </>
            )}

            {canEdit && (
              <Box sx={{ display: 'flex', gap: 1, pt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  onClick={() => setBackfillOpen(true)}
                  disabled={!defaultSourceUrl || inactiveCompany}
                  fullWidth
                >
                  Preview backfill
                </Button>
                <Button variant="outlined" onClick={onClose}>
                  Close
                </Button>
              </Box>
            )}
            {!defaultSourceUrl && canEdit && (
              <Typography variant="caption" color="warning.main" sx={{ mt: 1 }}>
                Add a press release URL or RSS feed on the company record before backfilling.
              </Typography>
            )}
          </Box>
        )}
      </Drawer>

      {company && (
        <BackfillPreviewDialog
          open={backfillOpen}
          onClose={() => setBackfillOpen(false)}
          company={company}
          defaultSourceUrl={defaultSourceUrl}
          onComplete={() => {
            onRefreshOverview();
            onBackfillComplete?.();
          }}
          onIssueReported={onIssueReported}
        />
      )}
    </>
  );
};

export default CompanyDetailDrawer;
